import React, { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import to from 'await-to-js';
import {
  pickAlign,
  useFontFamily,
  useEditableSubtitlesItems,
} from '@voomly/ui/player-deps';
import { NON_EXISTING_SUBTITLES_ID } from '@voomly/utils';
import { getCurrentTime } from '../../store/videoState/selectors';
import { getAreControlsVisible } from '../../store/videoControls/selectors';
import { IDefaultPropTypes } from '../../components/types/defaultPropTypes';
import { usePlayerSkin } from '../Controls/skins/PlayerSkinContext';
import {
  currentSubtitlesNameSelector,
  subtitlesIsVisibleSelector,
} from '../../store/subtitles/selectors';
import {
  getSubtitlesPublicRequest,
  IFsFileItemSubtitles,
} from '../../api/subtitles';
import { IPlayerWithVideo } from '../../types/player';

const StyledSubtitlesHolder = styled.div`
  position: absolute;
  z-index: 2; // $zSubtitles
  width: 100%;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  transition: transform 0.2s ease-out;
`;

const StyledSubtitles = styled.div`
  font-size: 0; // To remove space between inline-block elements
`;

const StyledSubtitlesLine = styled.span`
  display: inline-block;
  white-space: pre-wrap;
`;

const defaultSubtitlesAppearanceSettings =
  (): IPlayerWithVideo['skin']['subtitles'] => ({
    fontFamily: 'Roboto',
    fontSize: 16,
    fontStyle: [],
    textAlign: 'align-center',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    showByDefault: false,
  });

const SubtitlesComponent = ({
  buttonsScale,
  player,
  file,
}: IDefaultPropTypes) => {
  // NOTE: Courses fix, for some reason video player on courses doesn't have subtitles settings
  const appearanceSubtitles =
    player.skin.subtitles || defaultSubtitlesAppearanceSettings();
  const subtitles = player.subtitles as
    | IPlayerWithVideo['subtitles']
    | undefined;

  const editableSubtitlesItems = useEditableSubtitlesItems();

  useFontFamily(appearanceSubtitles.fontFamily);
  const skin = usePlayerSkin();
  const currentTime = useSelector(getCurrentTime);
  const areControlsVisible = useSelector(getAreControlsVisible);
  const isSubtitlesVisible = useSelector(subtitlesIsVisibleSelector);
  const currentSubtitlesName = useSelector(currentSubtitlesNameSelector);
  const [subtitlesContent, setSubtitlesContent] = useState<
    IFsFileItemSubtitles['content'] | undefined
  >(undefined);

  const enabledSubtitles = useMemo(() => {
    return subtitles?.items.filter((s) => s.isEnabled) || [];
  }, [subtitles?.items]);

  useEffect(() => {
    (async () => {
      setSubtitlesContent(undefined);
      if (currentSubtitlesName && isSubtitlesVisible) {
        const currentSubtitlesItem = enabledSubtitles.find(
          (s) => s.name === currentSubtitlesName
        );

        // On the funnel video player we are able to add subtitles with non-existing subtitlesId
        // example - `${shortid.generate()}|${NON_EXISTING_SUBTITLES_ID}`
        if (
          !currentSubtitlesItem ||
          currentSubtitlesItem.subtitlesId.includes(NON_EXISTING_SUBTITLES_ID)
        ) {
          return;
        }

        const [error, response] = await to(
          getSubtitlesPublicRequest(file.id, currentSubtitlesItem.subtitlesId)
        );

        if (error) {
          console.log('Subtitles error: ', error.message);
          return;
        }

        if (response) {
          setSubtitlesContent(response.data.content);
        }
      }
    })();
  }, [currentSubtitlesName, file.id, isSubtitlesVisible, enabledSubtitles]);

  const finalSubtitlesItems = useMemo(() => {
    if (editableSubtitlesItems.length) {
      return editableSubtitlesItems;
    }

    return subtitlesContent?.items || [];
  }, [editableSubtitlesItems, subtitlesContent]);

  if (!(isSubtitlesVisible || editableSubtitlesItems.length)) return null;

  const subtitlesPositionWithoutControls = 10;
  const subtitlesPositionWithControls =
    subtitlesPositionWithoutControls + skin.approximateBarHeight;

  return (
    <StyledSubtitlesHolder
      style={{
        textAlign: pickAlign([appearanceSubtitles.textAlign]),
        padding: `0 ${15 * buttonsScale}px`,
        transform: `translateY(-${
          areControlsVisible
            ? subtitlesPositionWithControls
            : subtitlesPositionWithoutControls
        }px)`,
      }}
    >
      {finalSubtitlesItems.map((el) => {
        if (currentTime >= el.startTime && currentTime < el.endTime) {
          return el.content.split('\n').map((text, index) => (
            <StyledSubtitles key={`${el.startTime}-${el.endTime}-${index}`}>
              <StyledSubtitlesLine
                style={{
                  backgroundColor: appearanceSubtitles.backgroundColor,
                  color: appearanceSubtitles.color,
                  fontFamily: appearanceSubtitles.fontFamily,
                  fontSize: `${appearanceSubtitles.fontSize * buttonsScale}px`,
                  lineHeight: `${
                    appearanceSubtitles.fontSize * 1.12 * buttonsScale
                  }px`,
                  fontWeight: appearanceSubtitles.fontStyle.includes('bold')
                    ? 'bold'
                    : undefined,
                  fontStyle: appearanceSubtitles.fontStyle.includes('italic')
                    ? 'italic'
                    : undefined,
                  textDecoration: `${
                    appearanceSubtitles.fontStyle.includes('underline')
                      ? 'underline'
                      : ''
                  } ${
                    appearanceSubtitles.fontStyle.includes('line-through')
                      ? 'line-through'
                      : ''
                  }`,
                  padding: `${2 * buttonsScale}px ${5 * buttonsScale}px`,
                }}
              >
                {text}
              </StyledSubtitlesLine>
            </StyledSubtitles>
          ));
        }
        return null;
      })}
    </StyledSubtitlesHolder>
  );
};

export const Subtitles = memo(SubtitlesComponent);
